import type Immutable from 'immutable';
import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';

import type { NarrativeThreadEnum, SceneAttributesResponse } from 'js/src/client';
import { ApiService } from 'js/src/client';
import { AssetSuffix } from 'js/src/editor/constants';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { getQueryParam } from 'js/src/libs/querystring';
import { getEditableMediaAssets, isOrdinarySceneGroup } from 'js/src/luminary';
import { getNarrativeThreadForSceneGroup } from 'js/src/luminary/luminary-utils';

export class SceneAttributesCacheData {
    templateId: string;
    aspectRatio: string;
    narrativeThread: NarrativeThreadEnum;
    autoCrop: boolean;
    recommendBrightness: boolean;
    assetUUIDs: string[];

    /*
    This class is just a convenient way of storing the values that are inputs to the call to
    the scene_attributes API. It allows us to pass around this package of values inside of the
    SceneAttributesCacheSingleton (which does the caching of the scene_attributes API output)
    */
    constructor(
        templateId: string,
        aspectRatio: string,
        narrativeThread: NarrativeThreadEnum,
        autoCrop: boolean,
        recommendBrightness: boolean,
        assetUUIDs: string[],
    ) {
        this.narrativeThread = narrativeThread;
        this.templateId = templateId;
        this.aspectRatio = aspectRatio;
        this.autoCrop = autoCrop;
        this.recommendBrightness = recommendBrightness;
        this.assetUUIDs = assetUUIDs;
    }

    static from(scene: Scene, luminary: Luminary, autoCrop = true, recommendBrightness = true) {
        const mediaAssets = getEditableMediaAssets(scene);

        // filter out media assets that we can't/shouldn't get scene attributes for
        const filteredAssets = mediaAssets
            // filter out "embedded_logo" assets which are basically watermarks
            .filter((asset) => !asset.name.endsWith(AssetSuffix.EmbeddedLogo))
            // sort by name so the backend and the frontend agree (or are at least more likely to ¯\_(ツ)_/¯)
            .sort((a, b) => {
                if (a.name < b.name) return -1;
                if (a.name > b.name) return 1;
                return 0;
            })
            // filter out any non-truthy uuids
            .map((asset) => asset.mediaId)
            .filter((mediaId) => mediaId) as string[];

        return new SceneAttributesCacheData(
            luminary.template.id,
            luminary.aspectRatio,
            getNarrativeThreadForSceneGroup(scene.sceneGroup),
            autoCrop,
            recommendBrightness,
            filteredAssets,
        );
    }

    equals(other: SceneAttributesCacheData) {
        return (
            other instanceof SceneAttributesCacheData && this.toCacheKey() === other.toCacheKey()
        );
    }

    toCacheKey() {
        return `${this.assetUUIDs.join(',')}-${this.templateId}-${
            this.aspectRatio
        }-${this.autoCrop}-${this.recommendBrightness}`;
    }
}

type CacheEntryType = Immutable.List<ImmutableMap<SceneAttributesResponse>>;
class SceneAttributesCacheSingleton {
    MAX_REQUESTS_IN_FLIGHT: number;
    MAX_CACHE_SIZE: number;
    cacheData: Record<string, CacheEntryType | Promise<CacheEntryType>>;
    prefetchQueue: Array<SceneAttributesCacheData>;
    luminary: Luminary | null;
    __luminaryStateChanged: () => void;

    constructor() {
        /*
        The cacheData will have a structure:
            {
                cacheKey: <Promise>, // if the data is being fetched
                cacheKey2: <sceneAttributesImmutable>, // if the data has already loaded
            }
        */
        this.MAX_REQUESTS_IN_FLIGHT = 2;
        this.MAX_CACHE_SIZE = 200; // this is probably ~3MB of data if the cache is full
        this.cacheData = {};
        this.prefetchQueue = [];
        this.luminary = null;
        this.__luminaryStateChanged = () => this._luminaryStateChanged();
    }

    setLuminary(luminary: Luminary) {
        if (this.luminary === luminary) {
            // we are trying to set the same instance of luminary to drive the
            // cache as the one already driving it! This is probably a calling-code error.
            throw Error(
                'trying to pass the same instance of Luminary into the SceneAttributesCache multiple times. You should only pass an instance of luminary once!',
            );
        }

        if (this.luminary) {
            // first, detach the current instance of luminary
            this.luminary.off('stateChanged', this.__luminaryStateChanged);
        }

        // now, hook up the new instance of luminary
        this.luminary = luminary;
        this.luminary.on('stateChanged', this.__luminaryStateChanged);
    }

    _luminaryStateChanged() {
        if (!this.luminary) throw Error('luminary is not set');
        const luminary = this.luminary;
        const ordinarySceneGroups = this.luminary.sceneGroups.filter((sceneGroup) =>
            isOrdinarySceneGroup(sceneGroup),
        );
        const scenesInOrdinarySceneGroups = ordinarySceneGroups.flatMap(
            (sceneGroup) => sceneGroup.scenes,
        );
        const sceneAttributesCacheDataList = scenesInOrdinarySceneGroups
            .map((scene) => SceneAttributesCacheData.from(scene, luminary))
            .filter((data) => data); // remove any null/undefined values

        this._limitQueueSizeWith(sceneAttributesCacheDataList);
        sceneAttributesCacheDataList.forEach((sceneAttributesCacheData) => {
            this._appendToQueue(sceneAttributesCacheData);
        });
    }

    async getSceneAttributes(sceneAttributesCacheData: SceneAttributesCacheData) {
        const cacheKey = sceneAttributesCacheData.toCacheKey();
        if (this.cacheData[cacheKey]) {
            if (this.cacheData[cacheKey] instanceof Promise) {
                return this.cacheData[cacheKey];
            } else {
                return Promise.resolve(this.cacheData[cacheKey]);
            }
        } else {
            return this._fetchAndStore(sceneAttributesCacheData);
        }
    }

    async _fetchAndStore(sceneAttributesCacheData: SceneAttributesCacheData) {
        const cacheKey = sceneAttributesCacheData.toCacheKey();

        this.cacheData[cacheKey] = new Promise<CacheEntryType>((resolve, reject) => {
            ApiService.sceneAttributesList({
                query: {
                    aspect_ratio: sceneAttributesCacheData.aspectRatio,
                    template_id: sceneAttributesCacheData.templateId,
                    auto_crop: sceneAttributesCacheData.autoCrop,
                    comma_separated_asset_uuids: sceneAttributesCacheData.assetUUIDs.join(','),
                    narrative_thread: sceneAttributesCacheData.narrativeThread,
                    recommend_brightness: sceneAttributesCacheData.recommendBrightness,
                },
            })
                .then((response) => {
                    const immutableResponse = ImmutableFromJS(response.data);
                    if (getQueryParam('disable_cache') !== 'true') {
                        this.cacheData[cacheKey] = immutableResponse;
                    }
                    resolve(immutableResponse);
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    // delete the key from the cache so that we will try again
                    // if this gets re-queried
                    delete this.cacheData[cacheKey];
                    reject(error);
                })
                .finally(() => {
                    // start any other prefetching work, whenever we get a free
                    // CPU cycle next
                    window.setTimeout(() => {
                        this._doPrefetchWork();
                    }, 0);
                });
        });
        return this.cacheData[cacheKey];
    }

    _appendToQueue(sceneAttributesCacheData: SceneAttributesCacheData) {
        // check if it is already stored in the cache
        const cacheKey = sceneAttributesCacheData.toCacheKey();
        if (this.cacheData[cacheKey]) {
            // we already have an entry in the cache for this data, just skip it.
            return;
        }

        // check if we already have an entry in the queue that matches
        for (let i = 0; i < this.prefetchQueue.length; i++) {
            if (sceneAttributesCacheData.equals(this.prefetchQueue[i])) {
                // yes, we have a matching entry in the cache. Skip this one.
                return;
            }
        }

        this.prefetchQueue.push(sceneAttributesCacheData);
        this._doPrefetchWork();
    }

    _doPrefetchWork() {
        const requestsInFlight = this._calculateInFlightRequestCount();

        if (requestsInFlight >= this.MAX_REQUESTS_IN_FLIGHT) {
            return;
        }

        const requestsToSpawn = this.MAX_REQUESTS_IN_FLIGHT - requestsInFlight;
        for (let i = 0; i < requestsToSpawn; i++) {
            const sceneAttributesCacheData = this.prefetchQueue.pop();
            if (!sceneAttributesCacheData) {
                // oh. There's nothing in the queue. Great, just ignore then!
                return;
            } else {
                const cacheKey = sceneAttributesCacheData.toCacheKey();
                if (!this.cacheData[cacheKey]) {
                    this._fetchAndStore(sceneAttributesCacheData);
                }
            }
        }
    }

    _calculateInFlightRequestCount() {
        let count = 0;
        for (const key in this.cacheData) {
            if (this.cacheData[key] instanceof Promise) {
                count++;
            }
        }

        return count;
    }

    _limitQueueSizeWith(sceneAttributesCacheDataList: SceneAttributesCacheData[]) {
        if (Object.keys(this.cacheData).length <= this.MAX_CACHE_SIZE) {
            return;
        }

        // if we get here, the cache is over the MAX_CACHE_SIZE, so we want to
        // clear out the values that are not currently relevant.
        const keyList = sceneAttributesCacheDataList.map((sceneAttributesCacheData) => {
            return sceneAttributesCacheData.toCacheKey();
        });

        const keysToDelete = [];
        for (const key in this.cacheData) {
            if (!keyList.includes(key)) {
                keysToDelete.push(key);
            }
        }

        keysToDelete.forEach((keyToDelete) => {
            delete this.cacheData[keyToDelete];
        });
    }

    destroy() {
        this.cacheData = {};
        this.prefetchQueue = [];
        this.luminary = null;
        this.__luminaryStateChanged = () => {};
    }
}

const SceneAttributesCache = (function () {
    let cacheInstance: SceneAttributesCacheSingleton;

    function createInstance() {
        return new SceneAttributesCacheSingleton();
    }

    return {
        getInstance: () => {
            if (!cacheInstance) {
                cacheInstance = createInstance();
            }
            return cacheInstance;
        },

        __resetInstance: () => {
            if (cacheInstance) {
                cacheInstance.destroy();
            }
            cacheInstance = createInstance();
        },
    };
})();

export default SceneAttributesCache;
